import { memo, useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import BuilderModePropertyTableMetadata from 'pages/BuilderMode/propertyTableMetadata';
import {
  FetchPlannerBlockMetadata,
  getBaseScenario,
  getPlannerBlockMetadataScenarioId,
  getToolbarType,
} from 'redux/PlannerModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import AddIndicatorTableFooterV1 from '../components/AddIndicatorTableFooterV1';
import { BUILDER_MODE_V1_LAYOUT } from '../constants';

import './BuilderModePropertyTableV1.scss';

/**
 * Reuses existing BuilderMode `propertyTableMetadata` logic,
 * but overrides legacy `Table.scss` look to match BuilderModeV1 (Figma 75644:18048).
 */
const BuilderModePropertyTableV1 = memo(() => {
  const { blockId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const baseScenario = useSelector(getBaseScenario);
  const blockMetadataScenarioId = useSelector(getPlannerBlockMetadataScenarioId);
  const toolbarType = useSelector(getToolbarType);

  useEffect(() => {
    if (!baseScenario?.id || !blockId || toolbarType !== 'Property') {
      return;
    }

    const metadataAlreadyLoadedForScenario = blockMetadataScenarioId === baseScenario.id;
    if (metadataAlreadyLoadedForScenario) return;

    dispatch(
      FetchPlannerBlockMetadata({
        blockId: Number(blockId),
        parameters: { scenario_id: baseScenario.id },
      }),
    );
  }, [baseScenario?.id, blockId, blockMetadataScenarioId, toolbarType, dispatch]);

  return (
    <Flex
      direction='column'
      gap={BUILDER_MODE_V1_LAYOUT.addIndicatorTableGap}
      width='100%'
      flex='0 1 auto'
      alignSelf='flex-start'
      maxH={BUILDER_MODE_V1_LAYOUT.tablePanelMaxHeight}
      minH={0}
    >
      <Box
        className='builder-mode-v1-property-table'
        width='100%'
        flex='1 1 auto'
        minH={0}
        overflow='hidden'
        display='flex'
        flexDirection='column'
        borderRadius='16px'
        border='1px solid'
        borderColor='blockOverview.border'
        bg='white'
      >
        <BuilderModePropertyTableMetadata useV1Checkbox hideTableAddRow />
      </Box>
      <AddIndicatorTableFooterV1 />
    </Flex>
  );
});

BuilderModePropertyTableV1.displayName = 'BuilderModePropertyTableV1';

export default BuilderModePropertyTableV1;
